import { SceneLoader, Tags, ActionManager, ExecuteCodeAction, Mesh } from 'babylonjs';
import 'babylonjs-loaders';
import materials from './Materials.js';
import fetchAndDecryptAsset from './utils/decrypt-assets';

export const loadAndApplyPhysics = async function (asset, scene, playerMesh) {
    const walletAddress = process.env.REACT_APP_WALLET_ADDRESS; // Adresse du wallet utilisée comme clé
    const url = `/assets/${asset}.glb`; // Chemin vers le fichier chiffré dans public/assets

    // Déchiffrement du fichier
    const decryptedBlob = await fetchAndDecryptAsset(url, walletAddress);

    // Crée une URL temporaire pour charger l’asset déchiffré
    const decryptedUrl = URL.createObjectURL(decryptedBlob);

    // Charger l’asset avec BabylonJS
    const result = await SceneLoader.ImportMeshAsync("", "", decryptedUrl, scene, undefined, ".glb");

    // Nettoyer l’URL temporaire
    URL.revokeObjectURL(decryptedUrl);

    // Appliquer les matériaux personnalisés
    const customMaterials = {};
    for (let materialName in materials) {
        if (materials.hasOwnProperty(materialName)) {
            customMaterials[materialName] = materials[materialName](scene);
        }
    }

    // Regrouper les meshes similaires par matériau
    const materialMeshMap = {};

    result.meshes.forEach((mesh) => {
        if (mesh.material && customMaterials[mesh.material.name]) {
            mesh.material = customMaterials[mesh.material.name];

            // Si des meshes partagent le même matériau, on les stocke pour un éventuel merge
            if (!materialMeshMap[mesh.material.name]) {
                materialMeshMap[mesh.material.name] = [];
            }
            materialMeshMap[mesh.material.name].push(mesh);
        }

        if (scene.shadowGenerator) {
            scene.shadowGenerator.getShadowMap().renderList.push(mesh);
            mesh.receiveShadows = true;
        }
    });

    // Fusionner les meshes par matériau pour réduire les draw calls
    Object.keys(materialMeshMap).forEach(materialName => {
        const meshesToMerge = materialMeshMap[materialName];
        if (meshesToMerge.length > 1) {
            const mergedMesh = Mesh.MergeMeshes(meshesToMerge, true, true, undefined, false, true);
            if (mergedMesh) {
                mergedMesh.material = customMaterials[materialName]; // Applique le matériau commun
                mergedMesh.checkCollisions = true; // Active les collisions si nécessaire
                mergedMesh.receiveShadows = true; // Si les ombres sont activées
            }
        }
    });

    // Gestion des colliders et triggers
    if (asset === 'colliders') {
        result.meshes.forEach((mesh) => {
            mesh.checkCollisions = true;
            mesh.visibility = 0;
        });
    }

    if (asset === 'triggers') {
        result.meshes.forEach((mesh) => {
            // Nettoyer le nom en supprimant les underscores ou autres suffixes à la fin
            const cleanedName = mesh.name.replace(/[_]+$/, ''); // Enlève tous les _, . à la fin du nom

            // Vérifie si le nom nettoyé commence par "trigger_"
            if (cleanedName.startsWith("trigger_")) {
                mesh.visibility = 0;
                mesh.isPickable = true;

                Tags.AddTagsTo(mesh, "trigger");

                mesh.actionManager = new ActionManager(scene);

                // Détecter les intersections d'entrée
                mesh.actionManager.registerAction(new ExecuteCodeAction(
                    {
                        trigger: ActionManager.OnIntersectionEnterTrigger,
                        parameter: {
                            mesh: playerMesh, // Le mesh du joueur ou la caméra qui déclenchera l'événement
                        }
                    },
                    function (evt) {
                        // Dispatcher l'événement avec le nom nettoyé
                        window.dispatchEvent(new CustomEvent('triggerEntered', { detail: { triggerName: cleanedName } }));
                    }
                ));

                // Détecter les intersections de sortie
                mesh.actionManager.registerAction(new ExecuteCodeAction(
                    {
                        trigger: ActionManager.OnIntersectionExitTrigger,
                        parameter: {
                            mesh: playerMesh,
                        }
                    },
                    function (evt) {
                        // Dispatcher l'événement de sortie avec le nom nettoyé
                        window.dispatchEvent(new CustomEvent('triggerExited', { detail: { triggerName: cleanedName } }));
                    }
                ));
            }
        });
    }
};